import { useState } from "react";
import axios from "axios";
import { Loader2, X, RefreshCw, Bot, Newspaper, Target, Shield, AlertTriangle, TrendingUp, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STRATEGY_LABEL = {
  "low-ball": { label: "🏷️ Low-ball agresivo", cls: "bg-rose-100 text-rose-700" },
  "premium":  { label: "💎 Premium / calidad",  cls: "bg-purple-100 text-purple-700" },
  "balanced": { label: "⚖️ Equilibrado",         cls: "bg-blue-100 text-blue-700" },
  "absent":   { label: "🚫 No competirá",       cls: "bg-gray-100 text-gray-600" },
};

const CONF_CLS = {
  "alta":  "bg-green-100 text-green-700",
  "media": "bg-amber-100 text-amber-700",
  "baja":  "bg-orange-100 text-orange-700",
};

export default function CompetitiveAnalysisModal({ oferta, onClose }) {
  const [data, setData] = useState(oferta?.competitive_analysis || null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const run = async (force = false) => {
    setLoading(!data); setRefreshing(force);
    try {
      const { data: res } = await axios.post(
        `${API}/ofertas-comercial/${oferta.id}/competitive-analysis${force ? "?force_refresh=true" : ""}`,
        {},
        { timeout: 70000 },
      );
      setData(res);
      if (force) toast.success("Análisis regenerado con la Pando");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error generando análisis");
    } finally { setLoading(false); setRefreshing(false); }
  };

  // Auto-load on first render if no cache
  if (!data && !loading && !refreshing) {
    run(false);
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl my-8 overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-500 to-orange-600 px-5 py-3.5 text-white flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <Bot size={18}/>
            <div className="min-w-0">
              <h2 className="font-bold text-sm" data-testid="ca-title">🤖 Análisis competitivo Pando</h2>
              <p className="text-xs text-white/85 truncate">{oferta?.cliente} · {oferta?.objeto_contrato}</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            {data && !loading && (
              <button onClick={() => run(true)} disabled={refreshing}
                      className="text-white/90 hover:text-white p-1.5 rounded hover:bg-white/10 transition-colors flex items-center gap-1 text-xs"
                      data-testid="ca-refresh">
                {refreshing ? <Loader2 size={13} className="animate-spin"/> : <RefreshCw size={13}/>}
                Regenerar
              </button>
            )}
            <button onClick={onClose} className="text-white/90 hover:text-white p-1.5 rounded hover:bg-white/10" data-testid="ca-close">
              <X size={16}/>
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4 max-h-[80vh] overflow-y-auto">
          {(loading || refreshing) && (
            <div className="py-12 text-center text-gray-500">
              <Loader2 size={28} className="animate-spin inline mb-2 text-amber-500"/>
              <p className="text-xs">{refreshing ? "Regenerando análisis con LLM…" : "Llamando a Claude para analizar competidores…"}</p>
              <p className="text-[10px] text-gray-400 mt-1">Cruza últimas 90 días de noticias scrapeadas con la oferta. ~10-30 s.</p>
            </div>
          )}

          {data && !loading && (
            <>
              {/* CC08 · Aviso de análisis obsoleto (anterior al cambio de reglas) */}
              {(data.stale || (data.generated_at && String(data.generated_at).slice(0,10) < "2026-10-13")) && (
                <div className="bg-amber-50 border border-amber-300 rounded-lg px-3 py-2 flex items-start gap-2" data-testid="ca-stale-banner">
                  <AlertTriangle size={14} className="text-amber-600 mt-0.5 shrink-0"/>
                  <div className="text-[11px] text-amber-800">
                    <b>Análisis posiblemente obsoleto.</b> {data.stale_reason || "Se generó antes del último cambio de reglas (13/10/2026) y puede contener conclusiones desactualizadas (p.ej. tratar un incumbente desconocido como ventaja)."} Pulsa <b>Regenerar</b> para actualizarlo.
                  </div>
                </div>
              )}
              {/* Stats top */}
              <div className="grid grid-cols-3 gap-2">
                <div className="bg-rose-50 border border-rose-200 rounded-lg p-2.5 text-center" data-testid="ca-comp-level">
                  <p className="text-[9px] uppercase font-semibold text-rose-700">Competitividad</p>
                  <p className="text-2xl font-bold text-rose-800 font-['Outfit']">{data.competitiveness_level || "—"}/5</p>
                  <p className="text-[10px] text-rose-600">{["Casi sin competencia","Poca","Moderada","Alta","Muy reñida"][Math.max(0,(data.competitiveness_level||3)-1)]}</p>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-2.5 text-center">
                  <p className="text-[9px] uppercase font-semibold text-blue-700">Estrategia precio rival</p>
                  <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-1 rounded ${STRATEGY_LABEL[data.predicted_strategy?.price_strategy]?.cls || "bg-gray-100"}`}>
                    {STRATEGY_LABEL[data.predicted_strategy?.price_strategy]?.label || data.predicted_strategy?.price_strategy || "—"}
                  </span>
                </div>
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 text-center">
                  <p className="text-[9px] uppercase font-semibold text-amber-700">Confianza</p>
                  <span className={`inline-block mt-1 text-xs font-bold px-2.5 py-1 rounded uppercase ${CONF_CLS[data.confidence] || "bg-gray-100"}`}>
                    {data.confidence || "—"}
                  </span>
                  {data._fallback && <p className="text-[9px] text-amber-700 mt-0.5">heurístico</p>}
                </div>
              </div>

              {/* Likely competitors */}
              <div className="bg-gray-50 rounded-lg p-3 border border-gray-100" data-testid="ca-competitors">
                <h3 className="text-xs font-bold text-gray-800 mb-2 flex items-center gap-1.5"><Target size={12}/> Competidores probables</h3>
                <div className="space-y-1.5">
                  {(data.likely_competitors || []).map((c, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <span className="bg-rose-600 text-white px-2 py-0.5 rounded text-[10px] font-bold flex-shrink-0 min-w-[110px] text-center">{c.name}</span>
                      <span className="bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px] font-semibold flex-shrink-0">{Math.round((c.probability||0)*100)}%</span>
                      <span className="text-gray-600 leading-snug">{c.rationale}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="bg-white border-l-4 border-l-amber-500 pl-3 py-1">
                <h3 className="text-[10px] uppercase tracking-wider font-bold text-gray-500 mb-1">Resumen competitivo</h3>
                <p className="text-xs text-gray-700 leading-relaxed">{data.competitive_summary}</p>
              </div>

              {/* Strategy threats */}
              {data.predicted_strategy?.key_threats?.length > 0 && (
                <div className="bg-rose-50/50 rounded-lg p-3 border border-rose-100">
                  <h3 className="text-xs font-bold text-rose-800 mb-1.5 flex items-center gap-1.5"><AlertTriangle size={12}/> Amenazas clave</h3>
                  <ul className="space-y-1 list-disc list-inside text-xs text-rose-900">
                    {data.predicted_strategy.key_threats.map((t,i) => <li key={i}>{t}</li>)}
                  </ul>
                  {data.predicted_strategy.price_strategy_rationale && (
                    <p className="text-[11px] text-rose-700 italic mt-2 pl-1">↳ {data.predicted_strategy.price_strategy_rationale}</p>
                  )}
                </div>
              )}

              {/* VSM Recommendations */}
              <div className="bg-emerald-50/60 rounded-lg p-3 border border-emerald-200" data-testid="ca-recommendations">
                <h3 className="text-xs font-bold text-emerald-800 mb-2 flex items-center gap-1.5"><Shield size={12}/> Recomendaciones VSM</h3>
                <p className="text-xs text-emerald-900 mb-2 font-medium">{data.vsm_recommendations?.approach}</p>
                {data.vsm_recommendations?.differentiators_to_emphasize?.length > 0 && (
                  <div className="mb-2">
                    <p className="text-[10px] uppercase font-semibold text-emerald-700 mb-1 flex items-center gap-1"><TrendingUp size={10}/>Diferenciadores</p>
                    <ul className="space-y-0.5 list-disc list-inside text-[11px] text-emerald-900 ml-1">
                      {data.vsm_recommendations.differentiators_to_emphasize.map((d,i) => <li key={i}>{d}</li>)}
                    </ul>
                  </div>
                )}
                {data.vsm_recommendations?.risks_to_mitigate?.length > 0 && (
                  <div>
                    <p className="text-[10px] uppercase font-semibold text-emerald-700 mb-1">Riesgos a mitigar</p>
                    <ul className="space-y-0.5 list-disc list-inside text-[11px] text-emerald-900 ml-1">
                      {data.vsm_recommendations.risks_to_mitigate.map((r,i) => <li key={i}>{r}</li>)}
                    </ul>
                  </div>
                )}
              </div>

              {/* News used */}
              {data.news_used?.length > 0 && (
                <div>
                  <h3 className="text-xs font-bold text-gray-700 mb-1.5 flex items-center gap-1.5"><Newspaper size={12}/> Noticias usadas en el análisis ({data.news_used.length})</h3>
                  <div className="space-y-1">
                    {data.news_used.map(n => (
                      <a key={n.id} href={n.url} target="_blank" rel="noreferrer"
                         className="flex items-start gap-2 p-1.5 hover:bg-gray-50 rounded text-[11px] border border-gray-100 group" data-testid={`ca-news-${n.id}`}>
                        <span className="bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded font-semibold text-[9px] uppercase flex-shrink-0">{(n.competitor||"").split(" ")[0]}</span>
                        <span className="flex-1 text-gray-700 group-hover:text-rose-700 line-clamp-1">{n.title}</span>
                        <ExternalLink size={10} className="text-gray-400 group-hover:text-rose-600 flex-shrink-0 mt-0.5"/>
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className="text-[10px] text-gray-400 text-center pt-2 border-t border-gray-100">
                Generado {data.from_cache ? "(caché 7d)" : "ahora"} · {(data.generated_at || "").slice(0,16).replace("T"," ")} · Powered by Claude
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
